import React, { useMemo } from "react";
import { Collapse } from "antd";
import Settings from "components/settings";
import { Localize } from "components/service";
import { timeFormat } from "utils/helper-functions";


const { Panel } = Collapse;
const renderNumber = Settings.Helpers.TranslationAiTable.renderNumber;
const renderPrice = Settings.Helpers.TranslationAiTable.renderPrice;
const formatHelper = Settings.Helpers.TranslationAiFormat;
const languageCacheHelper = Settings.Helpers.TranslationAiLanguageCache;
const pricingHelper = Settings.Helpers.TranslationAiPricing;

const toNumber = (value) => formatHelper.toNumber(value) || 0;

const buildLanguageSummary = (records, languageCacheStatusMap) => {
    const grouped = records.reduce((result, record) => {
        const slug = record?.lang_target ? String(record.lang_target).toLowerCase() : 'unknown';
        const language = languageCacheHelper.getLanguage(record?.lang_target);
        const current = result[ slug ] || {
            slug,
            label: language?.label || languageCacheHelper.getLanguageLabel(record?.lang_target),
            requests_count: 0,
            items_count: 0,
            cache_hit_items_count: 0,
            prompt_tokens: 0,
            completion_tokens: 0,
            total_tokens_price: 0,
            last_request_at: null,
            status: languageCacheStatusMap[ slug ]?.status,
        };
        const resolvedPrice = pricingHelper.resolvePrice(record);
        const createdAt = record?.created_at ? new Date(record.created_at) : null;
        const lastRequestAt = current.last_request_at ? new Date(current.last_request_at) : null;

        return {
            ...result,
            [ slug ]: {
                ...current,
                requests_count: current.requests_count + 1,
                items_count: current.items_count + toNumber(record?.items_count),
                cache_hit_items_count: current.cache_hit_items_count + toNumber(record?.cache_hit_items_count),
                prompt_tokens: current.prompt_tokens + toNumber(record?.prompt_tokens),
                completion_tokens: current.completion_tokens + toNumber(record?.completion_tokens),
                total_tokens_price: current.total_tokens_price + (resolvedPrice.value || 0),
                last_request_at: !lastRequestAt || createdAt > lastRequestAt ? record.created_at : current.last_request_at,
            },
        };
    }, {});

    return Object.values(grouped)
        .sort((a, b) => new Date(b.last_request_at || 0) - new Date(a.last_request_at || 0));
};

const TranslationAiLanguageRequestSummaryHeader = ({ count }) => (
    <div className="translation-ai-collapsible-header">
        <div className="title">
            <strong><Localize>TABLES.Title_TranslationAiLanguageSummary</Localize></strong>
            <span><Localize>TABLES.Description_TranslationAiLanguageSummary</Localize></span>
        </div>
        <div className="translation-ai-collapsible-hint">
            <strong>{ count }</strong>
            <Localize>TABLES.Value_Languages</Localize>
            <span><Localize>TABLES.Action_ShowDetails</Localize></span>
        </div>
    </div>
);

const TranslationAiLanguageRequestSummary = ({ records = [], languageCacheStatusMap = {} }) => {
    const summaryItems = useMemo(() => (
        buildLanguageSummary(records, languageCacheStatusMap)
    ), [ records, languageCacheStatusMap ]);

    return (
        <div className="translation-ai-request-overview">
            <div className="translation-ai-request-note">
                <strong><Localize>TABLES.Title_RequestBatchLog</Localize></strong>
                <span><Localize>TABLES.Description_RequestBatchLog</Localize></span>
            </div>

            { !!summaryItems.length &&
                <div className="translation-ai-language-request-summary">
                    <Collapse
                        bordered={ false }
                        className="translation-ai-language-cache-collapse"
                        expandIconPosition="right"
                        ghost>
                        <Panel
                            key="language-request-summary"
                            header={ <TranslationAiLanguageRequestSummaryHeader count={ summaryItems.length } /> }>
                            <div className="translation-ai-language-request-summary-list">
                                { summaryItems.map((item) => (
                                    <div className="translation-ai-language-request-summary-item" key={ item.slug }>
                                        <div className="language">
                                            <strong>{ item.label }</strong>
                                            { item.status && languageCacheHelper.renderStatusBadge(item.status) }
                                        </div>
                                        <span>
                                            <Localize>TABLES.Column_Title_Requests</Localize>
                                            <strong>{ renderNumber(item.requests_count) }</strong>
                                        </span>
                                        <span>
                                            <Localize>TABLES.Column_Title_Items</Localize>
                                            <strong>{ renderNumber(item.items_count) }</strong>
                                        </span>
                                        <span>
                                            <Localize>TABLES.Column_Title_CacheHits</Localize>
                                            <strong>{ renderNumber(item.cache_hit_items_count) }</strong>
                                        </span>
                                        <span>
                                            <Localize>TABLES.Column_Title_TotalPrice</Localize>
                                            <strong>{ renderPrice(item) }</strong>
                                        </span>
                                        <span>
                                            <Localize>TABLES.Column_Title_LastRequest</Localize>
                                            <strong>{ item.last_request_at ? timeFormat(item.last_request_at) : '\u2014' }</strong>
                                        </span>
                                    </div>
                                )) }
                            </div>
                        </Panel>
                    </Collapse>
                </div>
            }
        </div>
    );
};

export default TranslationAiLanguageRequestSummary;
